import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useLang } from "@/context/LanguageContext";
import RestaurantCard from "@/components/RestaurantCard";
import { Hotel, Home as HomeIcon, TreePalm, MapPin, ChevronRight, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";

const CATEGORY_IMG = {
  pizza: "https://images.unsplash.com/photo-1513104890138-7c749659a591?w=600&q=80",
  burgers: "https://images.unsplash.com/photo-1568901346375-23c9450c58cd?w=600&q=80",
  "fish-chips": "https://images.unsplash.com/photo-1580217593608-61931cefc821?w=600&q=80",
  indian: "https://images.unsplash.com/photo-1585937421612-70a008356fbe?w=600&q=80",
  steak: "https://images.unsplash.com/photo-1600891964092-4316c288032e?w=600&q=80",
  mexican: "https://images.unsplash.com/photo-1565299585323-38d6b0865b47?w=600&q=80",
  healthy: "https://images.unsplash.com/photo-1512621776951-a57141f2eefd?w=600&q=80",
  family: "https://images.unsplash.com/photo-1555939594-58d7cb561ad1?w=600&q=80",
  desserts: "https://images.unsplash.com/photo-1488477181946-6428a0291777?w=600&q=80",
  drinks: "https://images.unsplash.com/photo-1470337458703-46ad1756a187?w=600&q=80",
};

const Home = () => {
  const { t, cuisineLabel } = useLang();
  const nav = useNavigate();
  const [restaurants, setRestaurants] = useState([]);
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    api.get("/restaurants", { params: { featured: true } }).then(r => setRestaurants(r.data.slice(0, 6)));
    api.get("/categories").then(r => setCategories(r.data));
  }, []);

  const stayOptions = [
    { key: "hotel", icon: Hotel, label: t("hotel") },
    { key: "apartment", icon: HomeIcon, label: t("apartment") },
    { key: "villa", icon: TreePalm, label: t("villa") },
    { key: "other", icon: MapPin, label: t("other") },
  ];

  return (
    <div>
      {/* HERO */}
      <section className="relative overflow-hidden bg-gradient-to-br from-sand-50 via-white to-sand-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24 grid lg:grid-cols-2 gap-10 items-center">
          <div className="animate-fade-up">
            <div className="inline-flex items-center gap-2 bg-coral/10 text-coral px-3 py-1.5 rounded-full text-xs font-semibold mb-5">
              <Sparkles className="w-3 h-3" /> {t("slogan")}
            </div>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-extrabold text-navy leading-[1.05] tracking-tight">
              {t("heroTitle")}
            </h1>
            <p className="mt-5 text-navy/70 text-base sm:text-lg leading-relaxed max-w-lg">
              {t("heroSubtitle")}
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <Button onClick={() => nav("/restaurants")} className="btn-coral text-sm h-12 px-8" data-testid="hero-order-btn">{t("orderFood")}</Button>
              <Button onClick={() => nav("/restaurants")} variant="outline" className="rounded-full border-2 border-navy text-navy hover:bg-navy hover:text-white h-12 px-8" data-testid="hero-browse-btn">{t("browseRestaurants")}</Button>
            </div>
            <div className="mt-10">
              <p className="text-xs font-semibold uppercase tracking-widest text-navy/50 mb-3">{t("whereStaying")}</p>
              <div className="grid grid-cols-4 gap-2 max-w-md">
                {stayOptions.map((o) => {
                  const Icon = o.icon;
                  return (
                    <button key={o.key} onClick={() => nav("/restaurants")} className="flex flex-col items-center gap-1.5 p-3 rounded-xl bg-white border border-navy/10 hover:border-coral hover:-translate-y-0.5 transition-all" data-testid={`stay-${o.key}`}>
                      <Icon className="w-5 h-5 text-navy" />
                      <span className="text-[11px] font-medium text-navy">{o.label}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
          <div className="relative animate-fade-up">
            <div className="absolute -top-6 -left-6 w-24 h-24 bg-coral/20 rounded-full blur-2xl" />
            <div className="absolute -bottom-6 -right-6 w-32 h-32 bg-ocean/20 rounded-full blur-2xl" />
            <div className="relative grid grid-cols-2 gap-3">
              <img src="https://images.unsplash.com/photo-1513104890138-7c749659a591?w=800&q=80" alt="Pizza" className="rounded-3xl aspect-[3/4] object-cover shadow-lg" />
              <div className="flex flex-col gap-3 mt-8">
                <img src="https://images.unsplash.com/photo-1568901346375-23c9450c58cd?w=800&q=80" alt="Burger" className="rounded-3xl aspect-square object-cover shadow-lg" />
                <img src="https://images.unsplash.com/photo-1615540046913-40f49b1185a4?crop=entropy&cs=srgb&fm=jpg&q=80&w=800" alt="Caleta de Fuste" className="rounded-3xl aspect-square object-cover shadow-lg" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CATEGORIES */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
        <div className="flex items-end justify-between mb-8">
          <h2 className="font-heading text-2xl sm:text-4xl font-bold text-navy">{t("hungryFor")}</h2>
          <Link to="/categories" className="text-sm text-coral font-semibold flex items-center gap-1 hover:gap-2 transition-all">{t("viewAll")} <ChevronRight className="w-4 h-4" /></Link>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 sm:gap-4">
          {categories.slice(0, 10).map((c, i) => (
            <Link
              key={c.key}
              to={`/restaurants?cuisine=${c.key}`}
              data-testid={`category-${c.key}`}
              className="relative aspect-[4/5] rounded-2xl overflow-hidden group animate-fade-up"
              style={{ animationDelay: `${i * 40}ms` }}
            >
              <img src={CATEGORY_IMG[c.key]} alt={c.label} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" />
              <div className="absolute inset-0 bg-gradient-to-t from-navy/80 via-navy/20 to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-3">
                <div className="text-2xl mb-1">{c.icon}</div>
                <div className="font-heading text-white font-bold text-sm sm:text-base">{cuisineLabel(c.key)}</div>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* FEATURED */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
        <div className="flex items-end justify-between mb-8">
          <h2 className="font-heading text-2xl sm:text-4xl font-bold text-navy">{t("featuredRestaurants")}</h2>
          <Link to="/restaurants" className="text-sm text-coral font-semibold flex items-center gap-1 hover:gap-2 transition-all">{t("viewAll")} <ChevronRight className="w-4 h-4" /></Link>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {restaurants.map((r, i) => <RestaurantCard key={r.id} r={r} index={i} />)}
        </div>
      </section>

      {/* TOURIST SECTION */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
        <div className="relative rounded-3xl overflow-hidden bg-navy text-white p-8 sm:p-14 grid lg:grid-cols-2 gap-8 items-center">
          <div>
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-extrabold leading-tight">{t("touristTitle")}</h2>
            <p className="mt-4 text-white/70 max-w-md leading-relaxed">{t("touristText")}</p>
            <Button onClick={() => nav("/restaurants")} className="mt-6 btn-coral" data-testid="tourist-order-btn">{t("orderFood")}</Button>
          </div>
          <div className="grid grid-cols-3 gap-2">
            <img src="https://images.unsplash.com/photo-1615540046913-40f49b1185a4?crop=entropy&cs=srgb&fm=jpg&q=80&w=500" alt="Caleta beach" className="rounded-2xl aspect-square object-cover" />
            <img src="https://images.unsplash.com/photo-1615906187835-697fb34a57ab?crop=entropy&cs=srgb&fm=jpg&q=80&w=500" alt="Resort" className="rounded-2xl aspect-square object-cover mt-6" />
            <img src="https://images.unsplash.com/photo-1741231953820-e270cd4fe6e3?crop=entropy&cs=srgb&fm=jpg&q=80&w=500" alt="Dining" className="rounded-2xl aspect-square object-cover" />
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
        <h2 className="font-heading text-2xl sm:text-4xl font-bold text-navy mb-10">{t("howItWorks")}</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {[1, 2, 3, 4].map((n) => (
            <div key={n} className="p-6 rounded-2xl bg-sand-50 border border-navy/8" data-testid={`hiw-step-${n}`}>
              <div className="w-10 h-10 rounded-full bg-coral text-white font-bold flex items-center justify-center mb-3">{n}</div>
              <h3 className="font-heading font-bold text-navy text-lg mb-1">{t(`hiwStep${n}Title`)}</h3>
              <p className="text-sm text-navy/70">{t(`hiwStep${n}Desc`)}</p>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
};

export default Home;
